import { useMutation, useQueryClient } from '@tanstack/react-query';

import { ENDPOINT } from '~/constants/endpoints';

import { vestigasApi } from '~/services/kyClient';

import { queryKeysUser } from './queryKeys';

type UserProfilePicturePayload = File;

type UserProfilePictureResponse = {
  code: string;
  message: string;
  requestId: string;
};

/**
 * Updates user profile picture.
 *
 * @param {Object} picture - An file containing the user profile picture to update.
 * @returns {Promise} A promise that resolves with the API response.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/User/operation/upload_profile_picture_user_profile_picture_put
 */
export const updateUserProfilePicture = async (
  picture: UserProfilePicturePayload,
) => {
  const response = await vestigasApi
    .put(ENDPOINT.USER.UPDATE_PROFILE_PICTURE(), {
      body: picture,
      headers: { 'Content-Type': picture.type },
    })
    .json<UserProfilePictureResponse>();

  return response;
};

/**
 * Custom hook for updating the profile picture of a given user.
 *
 * @returns {Object} An object containing the mutation function and related properties.
 */
export const useMutationUpdateUserProfilePicture = (
  options?: Parameters<typeof useMutation>[0],
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (picture: UserProfilePicturePayload) =>
      updateUserProfilePicture(picture),
    onSuccess() {
      queryClient.invalidateQueries({
        queryKey: queryKeysUser.getProfilePicture(),
      });
    },
    ...options,
  });
};
